import React, { useState, useEffect, useRef } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { m } from 'framer-motion';
import Buttons from '../../Components/Button/Buttons';
import { fadeIn } from '../../Functions/GlobalAnimations';
import axios from 'axios';
import config from '../../config';
import { ToastContainer, toast } from 'react-toastify';
import {
  Trash2,
  ImageUp,
  FileTextIcon,
  FileIcon,
  ImageIcon,
} from 'lucide-react';
import { PDFDocument } from 'pdf-lib';

// Utility to format file size
const formatBytes = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// File icon mapping
const getFileIcon = (file) => {
  const fileType = file.type || '';
  const fileName = file.name || '';

  const iconMap = {
    pdf: {
      icon: FileTextIcon,
      conditions: (type, name) =>
        type.includes('pdf') || name.toLowerCase().endsWith('.pdf'),
    },
    image: {
      icon: ImageIcon,
      conditions: (type) => type.includes('image/'),
    },
  };

  for (const { icon: Icon, conditions } of Object.values(iconMap)) {
    if (conditions(fileType, fileName)) {
      return <Icon className="size-5 opacity-60" />;
    }
  }

  return <FileIcon className="size-5 opacity-60" />;
};

// File preview rendering
const getFilePreview = (file) => {
  const fileType = file.type || '';
  const fileName = file.name || '';
  const fileUrl = file instanceof File ? URL.createObjectURL(file) : '';

  const renderImage = (src) => (
    <img
      src={src}
      alt={fileName}
      className="w-full h-full rounded-t-md object-cover"
    />
  );

  return (
    <div className="bg-gray-100 flex aspect-square items-center justify-center overflow-hidden rounded-t-md">
      {fileType.includes('image/') && fileUrl ? renderImage(fileUrl) : getFileIcon(file)}
    </div>
  );
};

const ShoppingCart = (props) => {
  const [basket, setBasket] = useState([]);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null); // Single file
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState({ type: '', value: 0 }); // Track compression or upload progress
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const maxSizeMB = 25;
  const maxSize = maxSizeMB * 1024 * 1024; // 25MB
  const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg'];

  useEffect(() => {
    const fetchBasket = async () => {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        navigate('/login-register');
        return;
      }

      try {
        const response = await axios.get(`${config.REACT_APP_API_URL}/get-basket`, {
          headers: { Authorization: `Bearer ${token}`,
            'x-api-key': config.SITE_API_KEY },
        });
        setBasket(response.data.basket);
      } catch (error) {
        console.log('Erreur lors de la récupération du panier:', error);
        toast.error('Erreur lors de la récupération du panier');
      } finally {
        setLoading(false);
      }
    };

    fetchBasket();
  }, [navigate]);

  const handleQuantityChange = async (productId, newQuantity) => {
  // Convertir en nombre et vérifier si c'est valide
  const quantity = parseInt(newQuantity);
  
  // Ignorer si ce n'est pas un nombre valide ou si c'est inférieur à 1
  if (isNaN(quantity) || quantity < 1) return;

    const token = localStorage.getItem('accessToken');
    try {
      await axios.post(
        `${config.REACT_APP_API_URL}/update-quantity`,
        { productId, quantity },
        { headers: { Authorization: `Bearer ${token}`, 'x-api-key': config.SITE_API_KEY } }
      );
     setBasket(basket.map((item) => 
      item._id === productId ? { ...item, quantity } : item
    ));
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la quantité:', error);
      toast.error('Erreur lors de la mise à jour de la quantité');
    }
  };

  const handleQuantityInputChange = (productId, value) => {
  // Permet à l'utilisateur de taper, même des valeurs temporairement invalides
  // La validation se fera au blur ou quand la valeur est valide
  setBasket(basket.map((item) => 
    item._id === productId ? { ...item, inputValue: value } : item
  ));
};

const handleQuantityBlur = async (productId, value) => {
  const quantity = parseInt(value);
  
  // Si la valeur est invalide ou vide, remettre à 1
  if (!value || isNaN(quantity) || quantity < 1) {
    // Mettre à jour l'affichage avec la valeur par défaut
    setBasket(basket.map((item) => 
      item._id === productId ? { ...item, quantity: 1, inputValue: 1 } : item
    ));
    // Appeler l'API avec la valeur par défaut
    await handleQuantityChange(productId, 1);
  } else {
    // Si la valeur est valide, mettre à jour normalement
    await handleQuantityChange(productId, quantity);
  }
};

  const handleRemoveFromBasket = async (productId) => {
    const token = localStorage.getItem('accessToken');
    try {
      const response = await axios.post(
        `${config.REACT_APP_API_URL}/remove-from-basket`,
        { productId },
        { headers: { Authorization: `Bearer ${token}`,
            'x-api-key': config.SITE_API_KEY } }
      );
      setBasket(basket.filter((item) => item._id !== productId));
      toast.success(response.data.message);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast.error('Erreur lors de la suppression du produit');
    }
  };

  const compressPDF = async (file) => {
    try {
      setProgress({ type: 'compression', value: 0 });
      const arrayBuffer = await file.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer);

      // Optional: Optimize PDF (e.g., remove duplicate fonts, compress content)
      // Note: pdf-lib does not provide direct compression, but we can optimize
      const compressedPdfBytes = await pdfDoc.save({ updateFieldAppearances: false });

      setProgress({ type: 'compression', value: 50 });

      // Create a new File object from the compressed PDF
      const compressedFile = new File(
        [compressedPdfBytes],
        file.name,
        { type: 'application/pdf', lastModified: file.lastModified }
      );

      setProgress({ type: 'compression', value: 100 });

      return compressedFile;
    } catch (error) {
      setProgress({ type: '', value: 0 });
      throw new Error('Erreur lors de la compression du PDF');
    }
  };

  const handleFileUpload = async (selectedFile) => {
    const token = localStorage.getItem('accessToken');
    if (!selectedFile) {
      setError('Aucun fichier sélectionné');
      toast.error('Aucun fichier sélectionné');
      return;
    }

    if (selectedFile.size > maxSize) {
      setError(`Le fichier dépasse la taille maximale de ${maxSizeMB}MB`);
      toast.error(`Le fichier dépasse la taille maximale de ${maxSizeMB}MB`);
      return;
    }

    if (!allowedTypes.includes(selectedFile.type)) {
      setError('Type de fichier non autorisé (PDF, PNG, JPG uniquement)');
      toast.error('Type de fichier non autorisé');
      return;
    }

    let fileToUpload = selectedFile;

    // Compress PDF if the file is a PDF
    if (selectedFile.type.includes('pdf')) {
      try {
        fileToUpload = await compressPDF(selectedFile);
      } catch (error) {
        setError(error.message);
        toast.error(error.message);
        return;
      }
    }

    const formData = new FormData();
    formData.append('file', fileToUpload);

    try {
      setProgress({ type: 'upload', value: 0 });
      const response = await axios.post(`${config.REACT_APP_API_URL}/file_boncommande`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
            'x-api-key': config.SITE_API_KEY,
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setProgress({ type: 'upload', value: percentCompleted });
          }
        },
      });
      setFile(fileToUpload);
      localStorage.setItem('uploadedFile', fileToUpload.name);
      setError('');
      setProgress({ type: '', value: 0 });
      toast.success('Fichier téléchargé avec succès');
    } catch (error) {
      setProgress({ type: '', value: 0 });
      setError(error.response?.data?.message || 'Erreur lors du téléchargement du fichier');
      toast.error(error.response?.data?.message || 'Erreur lors du téléchargement du fichier');
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0]; // Single file
    if (droppedFile) {
      handleFileUpload(droppedFile);
    }
  };

  const openFileDialog = () => {
    fileInputRef.current.click();
  };

  const removeFile = () => {
    setFile(null);
    localStorage.removeItem('uploadedFile');
    setError('');
    setProgress({ type: '', value: 0 });
    fileInputRef.current.value = '';
  };

  if (loading) {
    return (
      <div style={props.style}>
        <section className="bg-lightgray py-[25px]">
          <Container>
            <Row className="items-center justify-center">
              <Col xl={8} lg={6}>
                <h1 className="font-serif text-darkgray font-medium mb-0 text-lg leading-[20px] md:text-center">Panier</h1>
              </Col>
            </Row>
          </Container>
        </section>
        <m.section className="py-[130px] lg:py-[90px] sm:py-[75px] xs:py-[50px]" {...fadeIn}>
          <Container>
            <Row>
              <Col>
                <p>Chargement du panier...</p>
              </Col>
            </Row>
          </Container>
        </m.section>
      </div>
    );
  }

  return (
    <div style={props.style}>
      <section className="bg-lightgray py-[25px]">
        <Container>
          <Row className="items-center justify-center">
            <Col xl={8} lg={6}>
              <h1 className="font-serif text-darkgray font-medium mb-0 text-lg leading-[20px] md:text-center">Panier</h1>
            </Col>
          </Row>
        </Container>
      </section>

      <m.section className="py-[130px] lg:py-[90px] sm:py-[75px] xs:py-[50px]" {...fadeIn}>
        <Container>
          <Row>
            <Col lg={8} className="pr-[20px] lg:pr-[30px] md:pr-[15px]">
              <Row>
                <Col xs={12} className="mb-[47px] md:mb-0">
                  <div className="flex flex-col lg:mt-20">
                    <div className="overflow-x-auto sm:mx-0.5 lg:mx-0.5">
                      <div className="py-2 inline-block min-w-full sm:px-6 lg:px-8">
                        <div className="overflow-hidden">
                          <table className="min-w-full">
                            <thead className="bg-white border-b">
                              <tr>
                                <th scope="col" className="text-gray-900 text-center border-b-2 whitespace-nowrap">N°</th>
                                <th scope="col" className="text-gray-900 text-center border-b-2 whitespace-nowrap">Image</th>
                                <th scope="col" className="text-gray-900 text-center border-b-2 whitespace-nowrap">Produit</th>
                                <th scope="col" className="text-gray-900 text-center border-b-2 whitespace-nowrap">Quantité</th>
                                <th scope="col" className="text-gray-900 text-center border-b-2 whitespace-nowrap">Unité</th>
                                <th scope="col" className="text-gray-900 text-center border-b-2 whitespace-nowrap">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {basket.length > 0 ? (
                                basket.map((item, i) => (
                                  <tr key={i} className="bg-white border-b intro-x">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-center">{i + 1}</td>
                                    <td className="text-sm text-gray-900 font-light px-6 py-4 whitespace-nowrap text-center">
                                      <Link to={`/single-product/${item._id}`}>
                                        <img
                                          src={item.picture === '/picture/product/' ? "/img/product.jpg" : `${config.REACT_APP_API_URL}${item.picture}`}
                                          className="w-[200px] h-[50px] min-lg:w-[100px] mx-auto"
                                          alt={item.name}
                                        />
                                      </Link>
                                    </td>
                                    <td className="text-sm text-gray-900 font-light px-6 py-4 whitespace-nowrap text-center">
                                      <Link to={`/single-product/${item._id}`}>{item.name}</Link>
                                    </td>
                                    <td className="text-sm text-gray-900 font-light px-6 py-4 whitespace-nowrap text-center">
                                      <div className="quantity">
                                        <input
                                          type="button"
                                          value="-"
                                          className="qty-minus qty-btn"
                                          onClick={() => handleQuantityChange(item._id, (item.quantity || 1) - 1)}
                                        />
                                        <input
                                          className="input-text qty-text"
                                          type="text"
                                          value={item.inputValue !== undefined ? item.inputValue : (item.quantity || 1)}
                                          onChange={(e) => handleQuantityInputChange(item._id, e.target.value)}
                                          onBlur={(e) => handleQuantityBlur(item._id, e.target.value)}
                                          min="1"
                                          inputMode="numeric"
                                        />
                                        <input
                                          type="button"
                                          value="+"
                                          className="qty-plus qty-btn"
                                          onClick={() => handleQuantityChange(item._id, (item.quantity || 1) + 1)}
                                        />
                                      </div>
                                    </td>
                                    <td className="text-sm text-gray-900 font-light px-6 py-4 whitespace-nowrap text-center">
                                      <Link to={`/single-product/${item._id}`}>{item.unit.name}</Link>
                                    </td>
                                    <td className="text-sm text-gray-900 font-light px-6 py-4 whitespace-nowrap text-center">
                                      <span
                                        onClick={() => handleRemoveFromBasket(item._id)}
                                        className="btn-default text-lg cursor-pointer hover:text-[#f00]"
                                      >
                                        <Trash2 className="mx-auto" />
                                      </span>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="6" className="text-center">Votre panier est vide.</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </Col>
            <Col lg={4}>
              <div className="bg-lightgray p-[50px] lg:py-[30px] lg:px-[20px] md:py-5">
                <span className="font-serif text-lg text-darkgray mb-[15px] inline-block font-medium">Total du panier</span>
                <table className="w-full total-price-table">
                  <tbody>
                    <tr className="total-amount">
                      <th className="font-medium text-darkgray">Total</th>
                      <td data-title="Total">
                        <h6 className="block font-sans font-medium mb-0 text-darkgray md:text-end">{basket.length} Produits</h6>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div className="mt-[20px]">
                  <Buttons
                    href="/checkout"
                    className="btn-fill tracking-[1px] font-medium font-serif rounded-none uppercase md:mb-[15px] !py-[13px] block w-full"
                    themeColor="#558927"
                    color="#fff"
                    size="lg"
                    title="Passer Commande"
                  />
                </div>
              </div>
              {/* File Upload Section */}
              <div className="mt-4 bg-white p-6 rounded-lg border border-dashed border-gray-300">
                <div
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  className={`flex flex-col items-center min-h-[150px] transition-colors ${isDragging ? 'bg-gray-100' : ''}`}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={(e) => handleFileUpload(e.target.files[0])}
                    accept=".pdf,.png,.jpg,.jpeg"
                    className="sr-only"
                    aria-label="Upload file"
                  />
                  {progress.type && (
                    <div className="w-full px-4 mb-4">
                      <p className="text-center">
                        {progress.type === 'compression' ? 'Compression en cours...' : 'Téléchargement en cours...'}
                      </p>
                      <progress
                        className="w-full h-4 rounded"
                        value={progress.value}
                        max="100"
                      />
                      <p className="text-center">{progress.value}%</p>
                    </div>
                  )}
                  {file ? (
                    <div className="w-full flex flex-col gap-3">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="truncate text-sm font-medium">Fichier téléchargé</h3>
                        <Buttons
                          onClick={removeFile}
                          className="btn-fill tracking-[1px] font-medium font-serif rounded-md uppercase py-2 px-4"
                          themeColor="#558927"
                          color="#fff"
                          size="sm"
                          title="Supprimer"
                        />
                      </div>
                      <div className="grid grid-cols-1 gap-4">
                        <div className="bg-white relative flex flex-col rounded-md border">
                          {getFilePreview(file)}
                          <div className="flex min-w-0 flex-col gap-0.5 border-t p-3">
                            <p className="truncate text-[13px] font-medium">{file.name}</p>
                            <p className="text-gray-500 truncate text-xs">{formatBytes(file.size)}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center px-4 py-3 text-center">
                      <ImageUp className="size-12 text-gray-300 mb-2" />
                      <p className="mb-1.5 text-sm font-medium">Déposez votre fichier ici</p>
                      <p className="text-gray-500 text-xs">PDF, PNG, JPG (Max {maxSizeMB}MB)</p>
                      <Buttons
                        onClick={openFileDialog}
                        className="btn-fill tracking-[1px] font-medium font-serif rounded-md uppercase mt-4 py-2 px-4"
                        themeColor="#558927"
                        color="#fff"
                        size="sm"
                        title="Sélectionner fichier"
                      />
                    </div>
                  )}
                </div>
                {error && (
                  <div className="text-red-500 text-xs mt-2" role="alert">
                    {error}
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Container>
        <ToastContainer position="bottom-left" />
      </m.section>
    </div>
  );
};

export default ShoppingCart;